/**
 * tableSkeleton - Helpers to render animated skeleton rows inside an Ant
 * Design `Table` while data is (re)loading.
 *
 * Instead of AntD's default spinner overlay, each column keeps its header and
 * width but renders a `Skeleton.Input` in every cell. Swapping the real rows
 * for these placeholders while a fetch is in flight gives users the sensation
 * that the table items are being refreshed.
 *
 * @module features/users/utils/tableSkeleton
 */
import { Skeleton } from 'antd';
import type { ColumnsType } from 'antd/es/table';

/** Row-key field injected on placeholder rows so each has a stable key. */
export const SKELETON_ROW_KEY = '__skeletonRowKey';

/** Placeholder-row count used when the real row count is unknown (e.g. first load). */
const DEFAULT_SKELETON_ROWS = 5;

export interface SkeletonTableProps<T> {
  /** Columns whose cells render a skeleton line (headers/widths preserved). */
  columns: ColumnsType<T>;
  /** Placeholder rows to feed the Table's `dataSource`. */
  dataSource: T[];
  /** Row key pointing at the injected {@link SKELETON_ROW_KEY} field. */
  rowKey: string;
}

/**
 * Builds Table props that render animated skeleton lines in place of real
 * rows while data is (re)loading.
 *
 * @param columns - The real table columns (headers/widths are preserved)
 * @param rowCount - How many placeholder rows to render; falls back to
 *   {@link DEFAULT_SKELETON_ROWS} when omitted or non-positive (typically the
 *   first load, where the previous row count is unknown)
 * @returns Table `columns`, `dataSource` and `rowKey` to spread while loading
 *
 * @example
 * ```tsx
 * const loading = tab.isLoading || tab.isRefreshing;
 * const rows = tab.data?.content ?? [];
 * const skeleton = getSkeletonTableProps(columns, rows.length);
 *
 * <Table
 *   columns={loading ? skeleton.columns : columns}
 *   dataSource={loading ? skeleton.dataSource : rows}
 *   rowKey={loading ? skeleton.rowKey : 'id'}
 * />
 * ```
 */
export function getSkeletonTableProps<T>(
  columns: ColumnsType<T>,
  rowCount?: number
): SkeletonTableProps<T> {
  const count = rowCount && rowCount > 0 ? rowCount : DEFAULT_SKELETON_ROWS;

  const skeletonColumns = columns.map((column) => ({
    ...column,
    render: () => <Skeleton.Input active size="small" block />,
  })) as ColumnsType<T>;

  const dataSource = Array.from({ length: count }, (_, index) => ({
    [SKELETON_ROW_KEY]: index,
  })) as unknown as T[];

  return { columns: skeletonColumns, dataSource, rowKey: SKELETON_ROW_KEY };
}
